import {
  RTCStatsType_inboundAudioRtp,
  RTCStatsType_inboundVideoRtp,
  RTCStatsType_outboundAudioRtp,
  RTCStatsType_outboundVideoRtp,
  RTCStatsType_remoteInboundAudioRtp,
  RTCStatsType_remoteInboundVideoRtp,
  RTCStatsType_remoteOutboundAudioRtp,
  RTCStatsType_remoteOutboundVideoRtp,
  RTCReceivedRtpStreamStats,
  RTCInboundRtpStreamStats,
  RTCInboundAudioRtpStreamStats,
  RTCInboundVideoRtpStreamStats,
  RTCSentRtpStreamStats,
  RTCOutboundRtpStreamStats,
  RTCOutboundVideoRtpStreamStats,
  RTCRemoteInboundRtpStreamStats,
  RTCRemoteOutboundRtpStreamStats,
  RTCStatsType_audioCodec,
  RTCStatsType_videoCodec,
  StatsReport,
  StatsReportMap
} from "./dto";
import { RemoteParticipantList } from "domain/room";

export enum StatsEvent {
  START = "start",
  STOP = "stop",
  TICK = "tick"
}

export class StatsRecorderService extends EventTarget {
  private intervalId: NodeJS.Timeout | null = null;
  private stats: StatsReportMap = new Map();
  private lastStats: Map<string, RTCStatsReport> = new Map();
  constructor(private interval: number = 500) {
    super();
  }

  public async start(participants: RemoteParticipantList): Promise<void> {
    this.dispatchEvent(new Event(StatsEvent.START));
    await this.getStatsFromParticipants(participants);
    this.intervalId = setInterval(async () => {
      await this.getStatsFromParticipants(participants);
    }, this.interval);
  }

  private async getStatsFromParticipants(
    participants: RemoteParticipantList
  ): Promise<void> {
    for (const participant of participants.getItems()) {
      const username = participant.displayName + "-" + participant.remoteId;
      const peerStats = await participant.peer.getStats();
      if (peerStats) {
        const stats = this.getStats(peerStats, username);
        const participantStats = this.stats.get(username);
        if (participantStats) {
          participantStats.push(stats);
        } else {
          this.stats.set(username, [stats]);
        }
        this.lastStats.set(username, peerStats);
      }
    }

    this.dispatchEvent(
      new CustomEvent<StatsReportMap>(StatsEvent.TICK, {
        detail: this.stats
      })
    );
  }

  public stop(): void {
    if (this.intervalId) clearInterval(this.intervalId);
    this.dispatchEvent(
      new CustomEvent<StatsReportMap>(StatsEvent.STOP, {
        detail: this.stats
      })
    );
  }

  private getStats(peerReport: RTCStatsReport, username: string): StatsReport {
    const statsObj = {
      inbound: {
        audio: {},
        video: {}
      },
      outbound: {
        audio: {},
        video: {}
      },
      remote_inbound: {
        audio: {},
        video: {}
      },
      remote_outbound: {
        audio: {},
        video: {}
      },
      timestamp: Date.now()
    };

    peerReport.forEach((stat) => {
      if (stat.type === "inbound-rtp" && stat.kind === "audio") {
        statsObj.inbound.audio = this.getAudioInboundStats(
          peerReport,
          stat,
          username
        );
      } else if (stat.type === "inbound-rtp" && stat.kind === "video") {
        statsObj.inbound.video = this.getVideoInboundStats(
          peerReport,
          stat,
          username
        );
      } else if (stat.type === "outbound-rtp" && stat.kind === "audio") {
        statsObj.outbound.audio = this.getAudioOutboundStats(
          peerReport,
          stat,
          username
        );
      } else if (stat.type === "outbound-rtp" && stat.kind === "video") {
        statsObj.outbound.video = this.getVideoOutboundStats(
          peerReport,
          stat,
          username
        );
      } else if (stat.type === "remote-inbound-rtp" && stat.kind === "audio") {
        statsObj.remote_inbound.audio = this.getAudioRemoteInboundStats(
          peerReport,
          stat,
          username
        );
      } else if (stat.type === "remote-inbound-rtp" && stat.kind === "video") {
        statsObj.remote_inbound.video = this.getVideoRemoteInboundStats(
          peerReport,
          stat,
          username
        );
      } else if (stat.type === "remote-outbound-rtp" && stat.kind === "audio") {
        statsObj.remote_outbound.audio = this.getAudioRemoteOutboundStats(
          peerReport,
          stat,
          username
        );
      } else if (stat.type === "remote-outbound-rtp" && stat.kind === "video") {
        statsObj.remote_outbound.video = this.getVideoRemoteOutboundStats(
          peerReport,
          stat,
          username
        );
      }
    });
    return statsObj as StatsReport;
  }

  private getAudioInboundStats(
    report: RTCStatsReport,
    stat: any,
    username: string
  ): RTCStatsType_inboundAudioRtp {
    let statsObj: Partial<RTCStatsType_inboundAudioRtp> = {};

    //RTCStats
    statsObj = { ...statsObj, ...this.getRTCStats(report, stat) };

    //RTCRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCRtpStreamStats(report, stat) };

    //RTCReceivedRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCReceivedRtpStreamStats(report, stat)
    };
    statsObj.packetsReceived_s = 0;

    //RTCInboundRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCInboundRtpStreamStats(report, stat)
    };
    statsObj.headerBytesReceived_in_kbits_s = 0;
    statsObj.packetsDiscarded_s = 0;
    statsObj.fecPacketsReceived_s = 0;
    statsObj.fecPacketsDiscarded_s = 0;
    statsObj.bytesReceived_in_kbits_s = 0;
    statsObj.jitterBufferDelay_jitterBufferEmittedCount_in_ms = 0;
    statsObj.jitterBufferTargetDelay_jitterBufferEmittedCount_in_ms = 0;
    statsObj.jitterBufferMinimumDelay_jitterBufferEmittedCount_in_ms = 0;
    statsObj.totalProcessingDelay_jitterBufferEmittedCount_in_ms = 0;
    statsObj.retransmittedPacketsReceived_s = 0;
    statsObj.retransmittedBytesReceived_in_kbits_s = 0;

    //RTCInboundAudioRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCInboundAudioRtpStreamStats(report, stat)
    };
    statsObj.totalSamplesReceived_s = 0;
    statsObj.concealedSamples_s = 0;
    statsObj.concealedSamples_totalSamplesReceived =
      stat.concealedSamples / stat.totalSamplesReceived;
    statsObj.silentConcealedSamples_s = 0;
    statsObj.insertedSamplesForDeceleration_s = 0;
    statsObj.removedSamplesForAcceleration_s = 0;
    statsObj.Audio_Level_in_RMS = Math.sqrt(
      stat.totalAudioEnergy / stat.totalSamplesDuration
    );

    //RTCStatsType_audioCodec
    statsObj = { ...statsObj, ...this.getRTCAudioCodecStats(report, stat) };

    // compute statistics over time
    const userLastStats = this.getLastStatsByUser(username);
    if (userLastStats && userLastStats.has(stat.id)) {
      const lastStats = userLastStats.get(stat.id);
      const duration = (stat.timestamp - lastStats.timestamp) / 1000; // in seconds

      //RTCReceivedRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCReceivedRtpStreamStatsOverTime(stat, lastStats, duration)
      };

      //RTCInboundRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCInboundRtpStreamStatsOverTime(stat, lastStats, duration)
      };

      //RTCInboundAudioRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCInboundAudioRtpStreamStatsOverTime(
          stat,
          lastStats,
          duration
        )
      };
    }
    return statsObj as RTCStatsType_inboundAudioRtp;
  }

  private getVideoInboundStats(
    report: RTCStatsReport,
    stat: any,
    username: string
  ): RTCStatsType_inboundVideoRtp {
    let statsObj: Partial<RTCStatsType_inboundVideoRtp> = {};

    //RTCStats
    statsObj = { ...statsObj, ...this.getRTCStats(report, stat) };

    //RTCRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCRtpStreamStats(report, stat) };

    //RTCReceivedRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCReceivedRtpStreamStats(report, stat)
    };
    statsObj.packetsReceived_s = 0;

    //RTCInboundRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCInboundRtpStreamStats(report, stat)
    };
    statsObj.headerBytesReceived_in_kbits_s = 0;
    statsObj.packetsDiscarded_s = 0;
    statsObj.fecPacketsReceived_s = 0;
    statsObj.fecPacketsDiscarded_s = 0;
    statsObj.bytesReceived_in_kbits_s = 0;
    statsObj.jitterBufferDelay_jitterBufferEmittedCount_in_ms = 0;
    statsObj.jitterBufferTargetDelay_jitterBufferEmittedCount_in_ms = 0;
    statsObj.jitterBufferMinimumDelay_jitterBufferEmittedCount_in_ms = 0;
    statsObj.totalProcessingDelay_jitterBufferEmittedCount_in_ms = 0;
    statsObj.retransmittedPacketsReceived_s = 0;
    statsObj.retransmittedBytesReceived_in_kbits_s = 0;

    //RTCInboundVideoRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCInboundVideoRtpStreamStats(report, stat)
    };
    statsObj.framesReceived_s = 0;
    statsObj.framesReceived_framesDecoded_framesDropped =
      stat.framesReceived - stat.framesDecoded - stat.framesDropped;
    statsObj.framesDecoded_s = 0;
    statsObj.keyFramesDecoded_s = 0;
    statsObj.totalDecodeTime_framesDecoded_in_ms =
      (1000 * stat.totalDecodeTime) / stat.framesDecoded;
    statsObj.totalAssemblyTime_framesAssembledFromMultiplePackets_in_ms =
      (1000 * stat.totalAssemblyTime) / stat.framesAssembledFromMultiplePackets;
    statsObj.totalInterFrameDelay_framesDecoded_in_ms =
      (1000 * stat.totalInterFrameDelay) / stat.framesDecoded;
    statsObj.interFrameDelayStDev_in_ms = Math.sqrt(
      ((stat.totalSquaredInterFrameDelay - stat.totalInterFrameDelay) ^
        (2 / stat.framesRendered)) /
        stat.framesRendered
    );
    statsObj.qpSum_framesDecoded = stat.qpSum / stat.framesDecoded;

    //RTCStatsType_videoCodec
    statsObj = { ...statsObj, ...this.getRTCVideoCodecStats(report, stat) };

    const userLastStats = this.getLastStatsByUser(username);
    if (userLastStats && userLastStats.has(stat.id)) {
      const lastStats = userLastStats.get(stat.id);
      const duration = (stat.timestamp - lastStats.timestamp) / 1000;

      //RTCReceivedRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCReceivedRtpStreamStatsOverTime(stat, lastStats, duration)
      };

      //RTCInboundRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCInboundRtpStreamStatsOverTime(stat, lastStats, duration)
      };

      //RTCInboundVideoRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCInboundVideoRtpStreamStatsOverTime(
          stat,
          lastStats,
          duration
        )
      };
    }
    //statsObj.resolution = `${stat.frameWidth}x${stat.frameHeight}`;
    return statsObj as RTCStatsType_inboundVideoRtp;
  }

  private getAudioOutboundStats(
    report: RTCStatsReport,
    stat: any,
    username: string
  ): RTCStatsType_outboundAudioRtp {
    let statsObj: Partial<RTCStatsType_outboundAudioRtp> = {};

    //RTCStats
    statsObj = { ...statsObj, ...this.getRTCStats(report, stat) };

    //RTCRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCRtpStreamStats(report, stat) };

    //RTCSentRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCSentRtpStreamStats(report, stat) };
    statsObj.packetsSent_s = 0;
    statsObj.bytesSent_in_kbits_s = 0;

    //RTCOutboundRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCOutboundRtpStreamStats(report, stat)
    };
    statsObj.headerBytesSent_in_kbits_s = 0;
    statsObj.retransmittedPacketsSent_s = 0;
    statsObj.retransmittedBytesSent_in_kbits_s = 0;
    statsObj.totalPacketSendDelay_packetsSent_in_ms =
      (1000 * stat.totalPacketSendDelay) / stat.packetsSent;

    //RTCStatsType_audioCodec
    statsObj = { ...statsObj, ...this.getRTCAudioCodecStats(report, stat) };

    const userLastStats = this.getLastStatsByUser(username);
    if (userLastStats && userLastStats.has(stat.id)) {
      const lastStats = userLastStats.get(stat.id);
      const duration = (stat.timestamp - lastStats.timestamp) / 1000;

      //RTCSentRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCSentRtpStreamStatsOverTime(stat, lastStats, duration)
      };

      //RTCOutboundRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCOutboundRtpStreamStatsOverTime(stat, lastStats, duration)
      };

      //RTCOutboundAudioRtpStreamStats
      //nothing to do here
    }
    return statsObj as RTCStatsType_outboundAudioRtp;
  }

  private getVideoOutboundStats(
    report: RTCStatsReport,
    stat: any,
    username: string
  ): RTCStatsType_outboundVideoRtp {
    let statsObj: Partial<RTCStatsType_outboundVideoRtp> = {};

    //RTCStats
    statsObj = { ...statsObj, ...this.getRTCStats(report, stat) };

    //RTCRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCRtpStreamStats(report, stat) };

    //RTCSentRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCSentRtpStreamStats(report, stat) };
    statsObj.packetsSent_s = 0;
    statsObj.bytesSent_in_kbits_s = 0;

    //RTCOutboundRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCOutboundRtpStreamStats(report, stat)
    };
    statsObj.headerBytesSent_in_kbits_s = 0;
    statsObj.retransmittedPacketsSent_s = 0;
    statsObj.retransmittedBytesSent_in_kbits_s = 0;
    statsObj.totalPacketSendDelay_packetsSent_in_ms = 0;

    //RTCOutboundVideoRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCOutboundVideoRtpStreamStats(report, stat)
    };
    statsObj.framesEncoded_s = 0;
    statsObj.totalEncodeTime_framesEncoded_in_ms =
      (1000 * stat.totalEncodeTime) / stat.framesEncoded;
    statsObj.totalEncodedBytesTarget_in_kbits_s = 0;
    statsObj.framesSent_s = 0;
    statsObj.totalPacketSendDelay_packetsSent_in_ms =
      (1000 * stat.totalPacketSendDelay) / stat.packetsSent;
    statsObj.qpSum_framesEncoded = stat.qpSum / stat.framesEncoded;

    //RTCStatsType_videoCodec
    statsObj = { ...statsObj, ...this.getRTCVideoCodecStats(report, stat) };

    const userLastStats = this.getLastStatsByUser(username);
    if (userLastStats && userLastStats.has(stat.id)) {
      const lastStats = userLastStats.get(stat.id);
      const duration = (stat.timestamp - lastStats.timestamp) / 1000;

      //RTCSentRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCSentRtpStreamStatsOverTime(stat, lastStats, duration)
      };

      //RTCOutboundRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCOutboundRtpStreamStatsOverTime(stat, lastStats, duration)
      };

      //RTCOutboundVideoRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCOutboundVideoRtpStreamStatsOverTime(
          stat,
          lastStats,
          duration
        )
      };
    }
    //statsObj.resolution = `${stat.frameWidth}x${stat.frameHeight}`;
    return statsObj as RTCStatsType_outboundVideoRtp;
  }

  private getAudioRemoteInboundStats(
    report: RTCStatsReport,
    stat: any,
    username: string
  ): RTCStatsType_remoteInboundAudioRtp {
    let statsObj: Partial<RTCStatsType_remoteInboundAudioRtp> = {};

    //RTCStats
    statsObj = { ...statsObj, ...this.getRTCStats(report, stat) };

    //RTCRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCRtpStreamStats(report, stat) };

    //RTCReceivedRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCReceivedRtpStreamStats(report, stat)
    };
    statsObj.packetsReceived_s = 0;

    //RTCRemoteInboundRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCRemoteInboundRtpStreamStats(report, stat)
    };

    //RTCStatsType_audioCodec
    statsObj = { ...statsObj, ...this.getRTCAudioCodecStats(report, stat) };

    const userLastStats = this.getLastStatsByUser(username);
    if (userLastStats && userLastStats.has(stat.id)) {
      const lastStats = userLastStats.get(stat.id);
      const duration = (stat.timestamp - lastStats.timestamp) / 1000;

      //RTCReceivedRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCReceivedRtpStreamStatsOverTime(stat, lastStats, duration)
      };
    }

    return statsObj as RTCStatsType_remoteInboundAudioRtp;
  }

  private getVideoRemoteInboundStats(
    report: RTCStatsReport,
    stat: any,
    username: string
  ): RTCStatsType_remoteInboundVideoRtp {
    let statsObj: Partial<RTCStatsType_remoteInboundVideoRtp> = {};

    //RTCStats
    statsObj = { ...statsObj, ...this.getRTCStats(report, stat) };

    //RTCRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCRtpStreamStats(report, stat) };

    //RTCReceivedRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCReceivedRtpStreamStats(report, stat)
    };
    statsObj.packetsReceived_s = 0;

    //RTCRemoteInboundRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCRemoteInboundRtpStreamStats(report, stat)
    };

    //RTCStatsType_videoCodec
    statsObj = { ...statsObj, ...this.getRTCVideoCodecStats(report, stat) };

    const userLastStats = this.getLastStatsByUser(username);
    if (userLastStats && userLastStats.has(stat.id)) {
      const lastStats = userLastStats.get(stat.id);
      const duration = (stat.timestamp - lastStats.timestamp) / 1000;

      //RTCReceivedRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCReceivedRtpStreamStatsOverTime(stat, lastStats, duration)
      };
    }

    return statsObj as RTCStatsType_remoteInboundVideoRtp;
  }

  private getAudioRemoteOutboundStats(
    report: RTCStatsReport,
    stat: any,
    username: string
  ): RTCStatsType_remoteOutboundAudioRtp {
    let statsObj: Partial<RTCStatsType_remoteOutboundAudioRtp> = {};

    //RTCStats
    statsObj = { ...statsObj, ...this.getRTCStats(report, stat) };

    //RTCRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCRtpStreamStats(report, stat) };

    //RTCSentRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCSentRtpStreamStats(report, stat) };
    statsObj.packetsSent_s = 0;
    statsObj.bytesSent_in_kbits_s = 0;

    //RTCRemoteOutboundRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCRemoteOutboundRtpStreamStats(report, stat)
    };

    //RTCStatsType_audioCodec
    statsObj = { ...statsObj, ...this.getRTCAudioCodecStats(report, stat) };

    const userLastStats = this.getLastStatsByUser(username);
    if (userLastStats && userLastStats.has(stat.id)) {
      const lastStats = userLastStats.get(stat.id);
      const duration = (stat.timestamp - lastStats.timestamp) / 1000;

      //RTCSentRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCSentRtpStreamStatsOverTime(stat, lastStats, duration)
      };
    }

    return statsObj as RTCStatsType_remoteOutboundAudioRtp;
  }

  private getVideoRemoteOutboundStats(
    report: RTCStatsReport,
    stat: any,
    username: string
  ): RTCStatsType_remoteOutboundVideoRtp {
    let statsObj: Partial<RTCStatsType_remoteOutboundVideoRtp> = {};

    //RTCStats
    statsObj = { ...statsObj, ...this.getRTCStats(report, stat) };

    //RTCRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCRtpStreamStats(report, stat) };

    //RTCSentRtpStreamStats
    statsObj = { ...statsObj, ...this.getRTCSentRtpStreamStats(report, stat) };
    statsObj.packetsSent_s = 0;
    statsObj.bytesSent_in_kbits_s = 0;

    //RTCRemoteOutboundRtpStreamStats
    statsObj = {
      ...statsObj,
      ...this.getRTCRemoteOutboundRtpStreamStats(report, stat)
    };

    //RTCStatsType_videoCodec
    statsObj = { ...statsObj, ...this.getRTCVideoCodecStats(report, stat) };

    const userLastStats = this.getLastStatsByUser(username);
    if (userLastStats && userLastStats.has(stat.id)) {
      const lastStats = userLastStats.get(stat.id);
      const duration = (stat.timestamp - lastStats.timestamp) / 1000;

      //RTCSentRtpStreamStats
      statsObj = {
        ...statsObj,
        ...this.getRTCSentRtpStreamStatsOverTime(stat, lastStats, duration)
      };
    }

    return statsObj as RTCStatsType_remoteOutboundVideoRtp;
  }

  //RTCStats
  private getRTCStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCStats> = {};
    statsObj.timestamp = stat.timestamp;
    statsObj.type = stat.type;
    statsObj.id = stat.id;
    return statsObj;
  }

  //RTCRtpStreamStats
  private getRTCRtpStreamStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCRtpStreamStats> = {};
    statsObj.ssrc = stat.ssrc;
    statsObj.kind = stat.kind;
    statsObj.transportId = stat.transportId;
    statsObj.codecId = stat.codecId;
    return statsObj;
  }

  //RTCReceivedRtpStreamStats
  private getRTCReceivedRtpStreamStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCReceivedRtpStreamStats> = {};
    statsObj.packetsReceived = stat.packetsReceived;
    statsObj.packetsLost = stat.packetsLost;
    statsObj.jitter = stat.jitter;
    return statsObj;
  }
  private getRTCReceivedRtpStreamStatsOverTime(
    stat: any,
    lastStats: any,
    duration: any
  ) {
    const statsObj: Partial<RTCReceivedRtpStreamStats> = {};
    statsObj.packetsReceived_s =
      (stat.packetsReceived - lastStats.packetsReceived) / duration;
    return statsObj;
  }

  //RTCSentRtpStreamStats
  private getRTCSentRtpStreamStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCSentRtpStreamStats> = {};
    statsObj.packetsSent = stat.packetsSent;
    statsObj.bytesSent = stat.bytesSent;
    return statsObj;
  }
  private getRTCSentRtpStreamStatsOverTime(
    stat: any,
    lastStats: any,
    duration: any
  ) {
    const statsObj: Partial<RTCSentRtpStreamStats> = {};
    statsObj.packetsSent_s =
      (stat.packetsSent - lastStats.packetsSent) / duration;
    statsObj.bytesSent_in_kbits_s =
      (8 * (stat.bytesSent - lastStats.bytesSent)) / duration / 1000;
    return statsObj;
  }

  //RTCInboundRtpStreamStats
  private getRTCInboundRtpStreamStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCInboundRtpStreamStats> = {};
    statsObj.trackIdentifier = stat.trackIdentifier;
    statsObj.mid = stat.mid;
    statsObj.remoteId = stat.remoteId;
    statsObj.lastPacketReceivedTimestamp = stat.lastPacketReceivedTimestamp;
    statsObj.headerBytesReceived = stat.headerBytesReceived;
    statsObj.packetsDiscarded = stat.packetsDiscarded;
    statsObj.fecBytesReceived = stat.fecBytesReceived;
    statsObj.fecPacketsReceived = stat.fecPacketsReceived;
    statsObj.fecPacketsDiscarded = stat.fecPacketsDiscarded;
    statsObj.bytesReceived = stat.bytesReceived;
    statsObj.nackCount = stat.nackCount;
    statsObj.totalProcessingDelay = stat.totalProcessingDelay;
    statsObj.estimatedPlayoutTimestamp = stat.estimatedPlayoutTimestamp;
    statsObj.jitterBufferDelay = stat.jitterBufferDelay;
    statsObj.jitterBufferTargetDelay = stat.jitterBufferTargetDelay;
    statsObj.jitterBufferEmittedCount = stat.jitterBufferEmittedCount;
    statsObj.jitterBufferMinimumDelay = stat.jitterBufferMinimumDelay;
    statsObj.retransmittedPacketsReceived = stat.retransmittedPacketsReceived;
    statsObj.retransmittedBytesReceived = stat.retransmittedBytesReceived;
    statsObj.rtxSsrc = stat.rtxSsrc;
    statsObj.fecSsrc = stat.fecSsrc;
    return statsObj;
  }
  private getRTCInboundRtpStreamStatsOverTime(
    stat: any,
    lastStats: any,
    duration: any
  ) {
    const statsObj: Partial<RTCInboundRtpStreamStats> = {};
    statsObj.headerBytesReceived_in_kbits_s =
      (8 * (stat.headerBytesReceived - lastStats.headerBytesReceived)) /
      duration /
      1000;
    statsObj.packetsDiscarded_s =
      (stat.packetsDiscarded - lastStats.packetsDiscarded) / duration;
    statsObj.fecPacketsReceived_s =
      (stat.fecPacketsReceived - lastStats.fecPacketsReceived) / duration;
    statsObj.fecPacketsDiscarded_s =
      (stat.fecPacketsDiscarded - lastStats.fecPacketsDiscarded) / duration;
    statsObj.bytesReceived_in_kbits_s =
      (8 * (stat.bytesReceived - lastStats.bytesReceived)) / duration / 1000;
    statsObj.retransmittedPacketsReceived_s =
      (stat.retransmittedPacketsReceived -
        lastStats.retransmittedPacketsReceived) /
      duration;
    statsObj.retransmittedBytesReceived_in_kbits_s =
      (8 *
        (stat.retransmittedBytesReceived -
          lastStats.retransmittedBytesReceived)) /
      duration /
      1000;
    return statsObj;
  }

  //RTCInboundAudioRtpStreamStats
  private getRTCInboundAudioRtpStreamStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCInboundAudioRtpStreamStats> = {};
    statsObj.totalSamplesReceived = stat.totalSamplesReceived;
    statsObj.concealedSamples = stat.concealedSamples;
    statsObj.silentConcealedSamples = stat.silentConcealedSamples;
    statsObj.concealmentEvents = stat.concealmentEvents;
    statsObj.insertedSamplesForDeceleration =
      stat.insertedSamplesForDeceleration;
    statsObj.removedSamplesForAcceleration = stat.removedSamplesForAcceleration;
    statsObj.audioLevel = stat.audioLevel;
    statsObj.totalAudioEnergy = stat.totalAudioEnergy;
    statsObj.totalSamplesDuration = stat.totalSamplesDuration;
    statsObj.playoutId = stat.playoutId;
    return statsObj;
  }
  private getRTCInboundAudioRtpStreamStatsOverTime(
    stat: any,
    lastStats: any,
    duration: any
  ) {
    const statsObj: Partial<RTCInboundAudioRtpStreamStats> = {};
    statsObj.totalSamplesReceived_s =
      (stat.totalSamplesReceived - lastStats.totalSamplesReceived) / duration;
    statsObj.concealedSamples_s =
      (stat.concealedSamples - lastStats.concealedSamples) / duration;
    statsObj.silentConcealedSamples_s =
      (stat.silentConcealedSamples - lastStats.silentConcealedSamples) /
      duration;
    statsObj.insertedSamplesForDeceleration_s =
      (stat.insertedSamplesForDeceleration -
        lastStats.insertedSamplesForDeceleration) /
      duration;
    statsObj.removedSamplesForAcceleration_s =
      (stat.removedSamplesForAcceleration -
        lastStats.removedSamplesForAcceleration) /
      duration;
    return statsObj;
  }

  //RTCInboundVideoRtpStreamStats
  private getRTCInboundVideoRtpStreamStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCInboundVideoRtpStreamStats> = {};
    statsObj.framesDecoded = stat.framesDecoded;
    statsObj.keyFramesDecoded = stat.keyFramesDecoded;
    statsObj.framesRendered = stat.framesRendered;
    statsObj.framesDropped = stat.framesDropped;
    statsObj.frameWidth = stat.frameWidth;
    statsObj.frameHeight = stat.frameHeight;
    statsObj.framesPerSecond = stat.framesPerSecond;
    statsObj.qpSum = stat.qpSum;
    statsObj.totalDecodeTime = stat.totalDecodeTime;
    statsObj.totalInterFrameDelay = stat.totalInterFrameDelay;
    statsObj.totalSquaredInterFrameDelay = stat.totalSquaredInterFrameDelay;
    statsObj.pauseCount = stat.pauseCount;
    statsObj.totalPausesDuration = stat.totalPausesDuration;
    statsObj.freezeCount = stat.freezeCount;
    statsObj.totalFreezesDuration = stat.totalFreezesDuration;
    statsObj.firCount = stat.firCount;
    statsObj.pliCount = stat.pliCount;
    statsObj.framesReceived = stat.framesReceived;
    statsObj.decoderImplementation = stat.decoderImplementation;
    statsObj.powerEfficientDecoder = stat.powerEfficientDecoder;
    statsObj.framesAssembledFromMultiplePackets =
      stat.framesAssembledFromMultiplePackets;
    statsObj.totalAssemblyTime = stat.totalAssemblyTime;
    return statsObj;
  }
  private getRTCInboundVideoRtpStreamStatsOverTime(
    stat: any,
    lastStats: any,
    duration: any
  ) {
    const statsObj: Partial<RTCInboundVideoRtpStreamStats> = {};
    statsObj.framesReceived_s =
      (stat.framesReceived - lastStats.framesReceived) / duration;
    statsObj.framesDecoded_s =
      (stat.framesDecoded - lastStats.framesDecoded) / duration;
    statsObj.keyFramesDecoded_s =
      (stat.keyFramesDecoded - lastStats.keyFramesDecoded) / duration;
    return statsObj;
  }

  //RTCOutboundRtpStreamStats
  private getRTCOutboundRtpStreamStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCOutboundRtpStreamStats> = {};
    statsObj.mid = stat.mid;
    statsObj.mediaSourceId = stat.mediaSourceId;
    statsObj.remoteId = stat.remoteId;
    statsObj.headerBytesSent = stat.headerBytesSent;
    statsObj.retransmittedPacketsSent = stat.retransmittedPacketsSent;
    statsObj.retransmittedBytesSent = stat.retransmittedBytesSent;
    statsObj.rtxSsrc = stat.rtxSsrc;
    statsObj.targetBitrate = stat.targetBitrate;
    statsObj.totalPacketSendDelay = stat.totalPacketSendDelay;
    statsObj.nackCount = stat.nackCount;
    statsObj.active = stat.active;
    return statsObj;
  }
  private getRTCOutboundRtpStreamStatsOverTime(
    stat: any,
    lastStats: any,
    duration: any
  ) {
    const statsObj: Partial<RTCOutboundRtpStreamStats> = {};
    statsObj.headerBytesSent_in_kbits_s =
      (8 * (stat.headerBytesSent - lastStats.headerBytesSent)) /
      duration /
      1000;
    statsObj.retransmittedPacketsSent_s =
      (stat.retransmittedPacketsSent - lastStats.retransmittedPacketsSent) /
      duration;
    statsObj.retransmittedBytesSent_in_kbits_s =
      (8 * (stat.retransmittedBytesSent - lastStats.retransmittedBytesSent)) /
      duration /
      1000;
    return statsObj;
  }

  //RTCOutboundVideoRtpStreamStats
  private getRTCOutboundVideoRtpStreamStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCOutboundVideoRtpStreamStats> = {};
    statsObj.rid = stat.rid;
    statsObj.totalEncodedBytesTarget = stat.totalEncodedBytesTarget;
    statsObj.frameWidth = stat.frameWidth;
    statsObj.frameHeight = stat.frameHeight;
    statsObj.framesPerSecond = stat.framesPerSecond;
    statsObj.framesSent = stat.framesSent;
    statsObj.hugeFramesSent = stat.hugeFramesSent;
    statsObj.framesEncoded = stat.framesEncoded;
    statsObj.keyFramesEncoded = stat.keyFramesEncoded;
    statsObj.qpSum = stat.qpSum;
    statsObj.totalEncodeTime = stat.totalEncodeTime;
    statsObj.qualityLimitationReason = stat.qualityLimitationReason;
    statsObj.qualityLimitationDurations = stat.qualityLimitationDurations;
    statsObj.qualityLimitationResolutionChanges =
      stat.qualityLimitationResolutionChanges;
    statsObj.firCount = stat.firCount;
    statsObj.pliCount = stat.pliCount;
    statsObj.encoderImplementation = stat.encoderImplementation;
    statsObj.powerEfficientEncoder = stat.powerEfficientEncoder;
    statsObj.scalabilityMode = stat.scalabilityMode;
    return statsObj;
  }
  private getRTCOutboundVideoRtpStreamStatsOverTime(
    stat: any,
    lastStats: any,
    duration: any
  ) {
    const statsObj: Partial<RTCOutboundVideoRtpStreamStats> = {};
    statsObj.framesEncoded_s =
      (stat.framesEncoded - lastStats.framesEncoded) / duration;
    statsObj.totalEncodedBytesTarget_in_kbits_s =
      (8 * (stat.totalEncodedBytesTarget - lastStats.totalEncodedBytesTarget)) /
      duration /
      1000;
    statsObj.framesSent_s = (stat.framesSent - lastStats.framesSent) / duration;
    return statsObj;
  }

  //RTCRemoteInboundRtpStreamStats
  private getRTCRemoteInboundRtpStreamStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCRemoteInboundRtpStreamStats> = {};
    statsObj.localId = stat.localId;
    statsObj.roundTripTime = stat.roundTripTime;
    statsObj.totalRoundTripTime = stat.totalRoundTripTime;
    statsObj.fractionLost = stat.fractionLost;
    statsObj.roundTripTimeMeasurements = stat.roundTripTimeMeasurements;
    return statsObj;
  }

  //RTCRemoteOutboundRtpStreamStats
  private getRTCRemoteOutboundRtpStreamStats(
    report: RTCStatsReport,
    stat: any
  ) {
    const statsObj: Partial<RTCRemoteOutboundRtpStreamStats> = {};
    statsObj.localId = stat.localId;
    statsObj.remoteTimestamp = stat.remoteTimestamp;
    statsObj.reportsSent = stat.reportsSent;
    statsObj.roundTripTime = stat.roundTripTime;
    statsObj.totalRoundTripTime = stat.totalRoundTripTime;
    statsObj.roundTripTimeMeasurements = stat.roundTripTimeMeasurements;
    return statsObj;
  }

  //RTCCodecAudioStats
  private getRTCAudioCodecStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCStatsType_audioCodec> = {};
    if (stat.codecId != undefined) {
      const codec = report.get(stat.codecId);
      statsObj.payloadType = codec.payloadType;
      statsObj.transportId = codec.transportId;
      statsObj.mimeType = codec.mimeType;
      statsObj.clockRate = codec.clockRate;
      statsObj.channels = codec.channels;
      statsObj.sdpFmtpLine = codec.sdpFmtpLine;
    }
    return statsObj;
  }

  //RTCCodecVideoStats
  private getRTCVideoCodecStats(report: RTCStatsReport, stat: any) {
    const statsObj: Partial<RTCStatsType_videoCodec> = {};
    if (stat.codecId != undefined) {
      const codec = report.get(stat.codecId);
      statsObj.payloadType = codec.payloadType;
      statsObj.transportId = codec.transportId;
      statsObj.mimeType = codec.mimeType;
      statsObj.clockRate = codec.clockRate;
      statsObj.channels = codec.channels;
      statsObj.sdpFmtpLine = codec.sdpFmtpLine;
    }
    return statsObj;
  }

  private getLastStatsByUser(username: string): RTCStatsReport | null {
    return this.lastStats.get(username) || null;
  }
}
